import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { SessionProvider, useSession } from "@/context/Session";
import { AppShell } from "@/components/AppShell";
import Website from "@/pages/Website";
import Capture from "@/pages/Capture";
import SopDetail from "@/pages/SopDetail";
import LibraryPage from "@/pages/Library";
import Chat from "@/pages/Chat";
import Review from "@/pages/Review";
import Team from "@/pages/Team";
import Checklist from "@/pages/Checklist";
import Billing, { PaymentResult } from "@/pages/Billing";
import Customers from "@/pages/Customers";
import CustomerChat from "@/pages/CustomerChat";
import Profile from "@/pages/Profile";
import Training from "@/pages/Training";
import JoinPage from "@/pages/Join";
import Properties from "@/pages/Properties";
import Coupons from "@/pages/Coupons";
import { TestLogin } from "@/components/TestLogin";
import { t } from "@/i18n";

function HomeRedirect() {
  return <Navigate to="/chat" replace />;
}

function Guard({ roles, children }) {
  const { me } = useSession();
  if (roles && !roles.includes(me.role)) return <Navigate to="/chat" replace />;
  return children;
}

function SignedInApp() {
  return (
    <SessionProvider>
      <AppShell>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/training" element={<Training />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/coupons" element={<Coupons />} />
          <Route path="/ask" element={<Navigate to="/chat" replace />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/sops/:id" element={<SopDetail />} />
          <Route path="/sops/:id/checklist" element={<Checklist />} />
          <Route path="/capture" element={<Capture />} />
          <Route path="/review" element={<Review />} />
          <Route path="/team" element={<Guard roles={["admin"]}><Team /></Guard>} />
          <Route path="/customers" element={<Guard roles={["admin", "manager"]}><Customers /></Guard>} />
          <Route path="/billing" element={<Guard roles={["admin"]}><Billing /></Guard>} />
          <Route path="/payment/success" element={<Guard roles={["admin"]}><PaymentResult ok /></Guard>} />
          <Route path="/payment/cancel" element={<Guard roles={["admin"]}><PaymentResult ok={false} /></Guard>} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </AppShell>
    </SessionProvider>
  );
}

const AuthPage = ({ children }) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
    <a href="/" className="flex items-center gap-2 font-bold text-xl tracking-tight"><img src="/mascot.png" alt="" className="h-10 w-auto" />SMBOI</a>
    {children}
  </div>
);

function SignedOut() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/chat")) return <CustomerChat />;
  // personal links look like /<organization>/<nickname>
  if (/^\/[a-z0-9-]+\/[a-z0-9-]+\/?$/.test(pathname)) return <Navigate to="/sign-in" replace />;
  return <Website />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <Routes>
            <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /><p className="text-xs faint uppercase tracking-widest" data-testid="test-login-divider">{t("or")}</p><TestLogin /></AuthPage>} />
            <Route path="/customer-chat" element={<CustomerChat />} />
            <Route path="/c/:slug" element={<CustomerChat />} />
            <Route path="/join/:slug" element={<JoinPage />} />
            <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
            <Route
              path="/*"
              element={
                <>
                  <Show when="signed-in"><SignedInApp /></Show>
                  <Show when="signed-out"><SignedOut /></Show>
                </>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
